/**
 * Agenda Inbox Store — badges, invitaciones y colas operativas (P12 §5/§6)
 *
 * Fuente de los badges del sidebar:
 * - invitationBadge → invitaciones de reunión pendientes
 * - assignedBadge   → acknowledgements pendientes + vencidas
 * - organizedBadge  → change requests + exception requests + reviews
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { agendaQueryService } from '@/services/api/agenda/agendaQuery.service'
import { meetingService } from '@/services/api/agenda/meeting.service'
import type {
  AgendaItem,
  AgendaQueueName,
  ConflictResolution,
  InboxSummary,
  MeetingRespondInput
} from '@/types/agenda'

export const useAgendaInboxStore = defineStore('agendaInbox', () => {
  // ==========================================================================
  // STATE
  // ==========================================================================

  const summary = ref<InboxSummary | null>(null)
  /** Participaciones de reunión pendientes de respuesta del usuario */
  const invitations = ref<AgendaItem[]>([])
  const queues = ref<Record<string, AgendaItem[]>>({})
  const loading = ref(false)
  const error = ref<string | null>(null)

  // ==========================================================================
  // GETTERS
  // ==========================================================================

  const invitationBadge = computed(() => summary.value?.invitationsPending ?? 0)

  const assignedBadge = computed(
    () => (summary.value?.acknowledgementPending ?? 0) + (summary.value?.overdue ?? 0)
  )

  const organizedBadge = computed(
    () =>
      (summary.value?.changeRequests ?? 0) +
      (summary.value?.exceptionRequests ?? 0) +
      (summary.value?.reviewPending ?? 0)
  )

  // ==========================================================================
  // ACTIONS
  // ==========================================================================

  async function loadSummary(): Promise<void> {
    try {
      summary.value = await agendaQueryService.getInboxSummary()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Error al cargar resumen de inbox'
    }
  }

  async function loadInbox(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      invitations.value = await agendaQueryService.getInbox()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Error al cargar inbox'
      invitations.value = []
    } finally {
      loading.value = false
    }
  }

  async function loadQueue(queue: AgendaQueueName): Promise<void> {
    loading.value = true
    error.value = null
    try {
      queues.value = { ...queues.value, [queue]: await agendaQueryService.getQueue(queue) }
    } catch (err) {
      error.value = err instanceof Error ? err.message : `Error al cargar cola ${queue}`
      queues.value = { ...queues.value, [queue]: [] }
    } finally {
      loading.value = false
    }
  }

  /**
   * Responde una invitación de reunión y refresca inbox + summary
   * (los badges deben reflejar el nuevo estado inmediatamente).
   */
  async function respondInvitation(
    meetingId: number,
    payload: MeetingRespondInput & { conflictResolution?: ConflictResolution }
  ): Promise<void> {
    await meetingService.respond(meetingId, payload)
    await Promise.all([loadInbox(), loadSummary()])
  }

  function reset(): void {
    summary.value = null
    invitations.value = []
    queues.value = {}
    loading.value = false
    error.value = null
  }

  return {
    // State
    summary,
    invitations,
    queues,
    loading,
    error,

    // Getters
    invitationBadge,
    assignedBadge,
    organizedBadge,

    // Actions
    loadSummary,
    loadInbox,
    loadQueue,
    respondInvitation,
    reset
  }
})
